import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  CheckboxGroup,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  NumberInput,
  NumberInputField,
  Progress,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  useToast,
  VStack,
  Badge,
  Wrap,
  WrapItem,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import { FiAlertTriangle, FiCheck, FiMapPin, FiUpload } from "react-icons/fi";
import Header from "./Header";
import { getApiBaseUrl, getPermissionApiBase, fetchApiJson } from "../utils/apiAssetUrl";
import { useNavigate } from "react-router-dom";

const GOOGLE_KEY = "AIzaSyD0AcxoBWhqkMIAfK-F-X6ya3Es-52o1YU";
const LIBRARIES = ["places"];

const PROJECT_TYPES = ["Film", "Web Series", "Ad", "Music Video", "Documentary"];
const LANGS = ["Hindi", "Gujarati", "English", "Tamil", "Telugu", "Marathi", "Bengali", "Kannada", "Malayalam", "Other"];
const LOCATION_TYPES = ["Public", "Private", "Government"];
const PERMISSION_OPTS = [
  { v: "police", label: "Police Permission" },
  { v: "traffic", label: "Traffic Police" },
  { v: "drone", label: "Drone (DGCA)" },
  { v: "municipal", label: "Municipal Corporation" },
  { v: "fire_noc", label: "Fire NOC" },
  { v: "local_authority", label: "Local Authority / Sarpanch" },
  { v: "asi", label: "ASI (heritage)" },
  { v: "airport_authority", label: "Airport Authority" },
];
const CREW_ROLES = ["Director", "DOP", "Sound", "Art Director", "Production Manager", "Assistant Director", "Makeup", "Costume", "Other"];
const CAMERA_OPTS = ["Cinema", "DSLR", "Drone", "Handheld"];
const VEHICLE_OPTS = ["Vanity Van", "Truck", "Car", "Bike"];
const STEP_LABELS = [
  "Project",
  "Identity",
  "Location",
  "Permissions",
  "Schedule",
  "Crew",
  "Equipment",
  "Vehicles",
  "Special",
  "Utilities",
  "Legal",
  "Final",
];

function emptyForm() {
  return {
    project_title: "",
    project_type: "",
    production_house: "",
    producer_name: "",
    director_name: "",
    line_producer: "",
    language: [],
    description: "",
    applicant_type: "individual",
    contact_number: "",
    email: "",
    email_verified: false,
    documents: { identity: {}, legal: {}, final: {} },
    location_name: "",
    address: "",
    city: "",
    state: "",
    landmark: "",
    location_lat: "",
    location_lng: "",
    location_type: "",
    indoor_outdoor: "outdoor",
    area_usage: { mode: "full", partialDetail: "" },
    permissions_required: [],
    heritage_site: false,
    near_airport_hint: false,
    start_datetime: "",
    end_datetime: "",
    total_days: 0,
    daily_timing: "",
    night_shoot: false,
    crew_size: "",
    crew_details: [{ name: "", role: "" }],
    equipment: { cameraTypes: [], equipmentTags: [], droneDetails: { registrationId: "", pilotName: "" } },
    lighting: "normal",
    generator_required: false,
    vehicles: { count: "", types: [], parkingRequired: false, entryPermission: false },
    special_requirements: {
      loudSound: false,
      roadBlocking: false,
      crowdControl: false,
      stunts: false,
      fireExplosives: false,
      animals: false,
    },
    utilities: { electricity: false, generatorUsage: "", water: "", washroom: "" },
  };
}

function useDebouncedCallback(fn, delay) {
  const t = useRef();
  const fnRef = useRef(fn);
  fnRef.current = fn;
  return useCallback(
    (...args) => {
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay]
  );
}

/** CheckboxGroup + MySQL JSON can yield non-arrays — normalize so Hindi (first item) is not stuck on. */
function toLanguageArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === "object") return Object.values(raw).map(String).filter(Boolean);
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return [];
    try {
      const j = JSON.parse(s);
      if (Array.isArray(j)) return j.map(String).filter(Boolean);
      if (j && typeof j === "object") return Object.values(j).map(String).filter(Boolean);
    } catch {
      return [s];
    }
  }
  return [];
}

export default function LocationPermissionRequest() {
  const urlBase = getApiBaseUrl();
  const permBase = getPermissionApiBase();
  const toast = useToast();
  const navigate = useNavigate();
  const login = useMemo(() => JSON.parse(localStorage.getItem("login")) || "", []);

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [recordId, setRecordId] = useState(null);
  const [requestRef, setRequestRef] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [tagInput, setTagInput] = useState("");
  const recordIdRef = useRef(null);

  const footerBottom = useBreakpointValue({
    base: "calc(76px + env(safe-area-inset-bottom, 0px))",
    md: 0,
  });
  const footerZ = useBreakpointValue({ base: 1002, md: 1002 });
  const locRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
  });

  const setF = useCallback((patch) => setForm((p) => ({ ...p, ...patch })), []);
  const docs = form.documents || { identity: {}, legal: {}, final: {} };

  const persistPayload = useCallback(() => {
    const uid = login?.permanent_ID ? String(login.permanent_ID) : "";
    return {
      user_id: uid,
      project_title: form.project_title,
      project_type: form.project_type,
      production_house: form.production_house,
      producer_name: form.producer_name,
      director_name: form.director_name,
      line_producer: form.line_producer,
      language: toLanguageArray(form.language),
      description: form.description,
      applicant_type: form.applicant_type,
      contact_number: form.contact_number,
      email: form.email,
      phone_verified: true,
      email_verified: form.email_verified,
      location_name: form.location_name,
      address: form.address,
      city: form.city,
      state: form.state,
      landmark: form.landmark,
      location_lat: form.location_lat,
      location_lng: form.location_lng,
      location_type: form.location_type,
      indoor_outdoor: form.indoor_outdoor,
      area_usage: form.area_usage,
      permissions_required: form.permissions_required,
      near_airport_hint: form.near_airport_hint,
      start_datetime: form.start_datetime || null,
      end_datetime: form.end_datetime || null,
      total_days: form.total_days,
      daily_timing: form.daily_timing,
      night_shoot: form.night_shoot,
      crew_size: form.crew_size === "" ? null : Number(form.crew_size),
      crew_details: form.crew_details,
      equipment: form.equipment,
      drone_details: form.equipment?.droneDetails || {},
      lighting: form.lighting,
      generator_required: form.generator_required,
      vehicles: form.vehicles,
      special_requirements: form.special_requirements,
      utilities: form.utilities,
      documents: form.documents,
      status: "draft",
    };
  }, [form, login]);

  const saveDraft = useCallback(async () => {
    const body = persistPayload();
    const id = recordIdRef.current;
    setSaving(true);
    try {
      if (!id) {
        const { res, data: j } = await fetchApiJson(`${permBase}/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok || j.isError) throw new Error(j.message || "Save failed");
        recordIdRef.current = j.id;
        setRecordId(j.id);
        setRequestRef(j.request_ref || "");
        return j;
      }
      const { res, data: j } = await fetchApiJson(`${permBase}/update/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok || j.isError) throw new Error(j.message || "Save failed");
      return j;
    } catch (e) {
      toast({ title: "Could not autosave", description: e.message, status: "error" });
      return null;
    } finally {
      setSaving(false);
    }
  }, [persistPayload, permBase, toast]);

  const debouncedSave = useDebouncedCallback(() => saveDraft(), 800);

  useEffect(() => {
    recordIdRef.current = recordId;
  }, [recordId]);

  useEffect(() => {
    if (login) {
      setForm((p) => ({
        ...p,
        email: p.email || login.email || "",
        contact_number: p.contact_number || login.mobile || "",
        producer_name: p.producer_name || login.name || "",
      }));
    }
  }, [login]);

  useEffect(() => {
    const uid = login?.permanent_ID;
    if (!uid) return;
    (async () => {
      try {
        const { res: r, data: j } = await fetchApiJson(
          `${permBase}/my/latest-draft?user_id=${encodeURIComponent(uid)}`
        );
        if (!r.ok) throw new Error("Draft load failed");
        if (j.data && j.data.id) {
          const d = j.data;
          setRecordId(d.id);
          setRequestRef(d.request_ref || "");
          setForm({
            ...emptyForm(),
            ...d,
            language: toLanguageArray(d.language),
            area_usage: d.area_usage || { mode: "full", partialDetail: "" },
            permissions_required: d.permissions_required || [],
            crew_details: d.crew_details?.length ? d.crew_details : [{ name: "", role: "" }],
            equipment: d.equipment || { cameraTypes: [], equipmentTags: [], droneDetails: d.drone_details || {} },
            vehicles: d.vehicles || emptyForm().vehicles,
            special_requirements: d.special_requirements || emptyForm().special_requirements,
            utilities: d.utilities || emptyForm().utilities,
            documents: d.documents || { identity: {}, legal: {}, final: {} },
            start_datetime: d.start_datetime ? d.start_datetime.slice(0, 16) : "",
            end_datetime: d.end_datetime ? d.end_datetime.slice(0, 16) : "",
          });
        }
      } catch (_) {}
    })();
  }, [login, permBase]);

  useEffect(() => {
    const hasContent =
      recordId != null ||
      Boolean(form.project_title?.trim()) ||
      Boolean(form.email?.trim()) ||
      Boolean(form.contact_number?.trim()) ||
      Boolean(form.description?.trim()) ||
      step > 0;
    if (!hasContent) return;
    debouncedSave();
  }, [form, step, recordId, debouncedSave]);

  const onPlaceChanged = useCallback(() => {
    const ac = autocompleteRef.current;
    if (!ac) return;
    const place = ac.getPlace();
    if (!place?.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    let city = "";
    let state = "";
    (place.address_components || []).forEach((c) => {
      if (c.types.includes("locality")) city = c.long_name;
      if (c.types.includes("administrative_area_level_1")) state = c.long_name;
    });
    const types = place.types || [];
    const addr = place.formatted_address || "";
    const nearAir =
      types.includes("airport") ||
      /airport/i.test(addr) ||
      types.includes("point_of_interest");
    setForm((p) => {
      let perms = [...(p.permissions_required || [])];
      if (nearAir && !perms.includes("airport_authority")) {
        perms.push("airport_authority");
        toast({ title: "Airport proximity", description: "Airport Authority added — review in Permissions step.", status: "info", duration: 4000 });
      }
      return {
        ...p,
        location_name: place.name || p.location_name,
        address: addr,
        city: city || p.city,
        state: state || p.state,
        location_lat: String(lat),
        location_lng: String(lng),
        near_airport_hint: nearAir,
        permissions_required: perms,
      };
    });
  }, [toast]);

  const upload = async (file, slot) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    let res;
    let j;
    try {
      ({ res, data: j } = await fetchApiJson(`${permBase}/upload`, { method: "POST", body: fd }));
    } catch (e) {
      toast({ title: "Upload failed", description: e.message, status: "error" });
      return;
    }
    if (!res.ok || j.isError) {
      toast({ title: "Upload failed", status: "error" });
      return;
    }
    setForm((p) => {
      const d = { ...(p.documents || { identity: {}, legal: {}, final: {} }) };
      const [section, key] = slot.split(".");
      d[section] = { ...d[section], [key]: j.path };
      return { ...p, documents: d };
    });
    toast({ title: "Uploaded", status: "success" });
  };

  const validateStep = (s) => {
    switch (s) {
      case 0:
        if (!form.project_title?.trim()) return "Project title required";
        if (!form.project_type) return "Project type required";
        if (!form.language?.length) return "Select at least one language";
        return "";
      case 1: {
        const ph = String(form.contact_number || "").replace(/\D/g, "");
        if (ph.length < 10) return "Valid contact number required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || "")) return "Valid email required";
        if (!form.email_verified) return "Verify email with OTP";
        const idn = docs.identity || {};
        const need = ["aadhaar", "pan", "producer_card", "authority_letter", "company_letterhead", "address_proof"];
        for (const k of need) {
          if (!idn[k]) return `Upload ${k.replace(/_/g, " ")}`;
        }
        if (form.applicant_type === "company" && !idn.gst_certificate) return "GST certificate required for company";
        return "";
      }
      case 2:
        if (!form.location_name?.trim()) return "Location name required";
        if (!form.address?.trim()) return "Address required";
        if (!form.location_type) return "Location type required";
        if (form.area_usage?.mode === "partial" && !form.area_usage?.partialDetail?.trim())
          return "Describe partial area";
        return "";
      case 3:
        if (!form.permissions_required?.length) return "Select at least one permission";
        return "";
      case 4:
        if (!form.start_datetime || !form.end_datetime) return "Start and end required";
        if (new Date(form.end_datetime) <= new Date(form.start_datetime)) return "End must be after start";
        if (!form.daily_timing) return "Daily timing required";
        return "";
      case 5:
        if (!form.crew_size || Number(form.crew_size) < 1) return "Crew size required";
        if (!form.crew_details?.some((c) => c.name && c.role)) return "Add at least one key crew member";
        return "";
      case 6:
        if (!form.equipment?.cameraTypes?.length) return "Select camera type(s)";
        return "";
      case 7:
        if (!form.vehicles?.count || Number(form.vehicles.count) < 0) return "Vehicle count required";
        return "";
      case 10: {
        const lg = docs.legal || {};
        if (!lg.indemnity_bond || !lg.insurance_certificate) return "Indemnity & insurance required";
        return "";
      }
      default:
        return "";
    }
  };

  const goNext = async () => {
    const err = validateStep(step);
    if (err) {
      toast({ title: err, status: "warning" });
      return;
    }
    await saveDraft();
    setStep((x) => Math.min(x + 1, STEP_LABELS.length - 1));
  };

  const goBack = () => setStep((x) => Math.max(x - 1, 0));

  useEffect(() => {
    if (!form.start_datetime || !form.end_datetime) return;
    const a = new Date(form.start_datetime);
    const b = new Date(form.end_datetime);
    if (b > a) {
      const days = Math.max(1, Math.ceil((b - a) / (86400000)));
      setForm((p) => (p.total_days !== days ? { ...p, total_days: days } : p));
    }
  }, [form.start_datetime, form.end_datetime]);

  useEffect(() => {
    setForm((p) => {
      let perms = [...(p.permissions_required || [])];
      if (p.heritage_site) {
        if (!perms.includes("asi")) perms.push("asi");
      } else {
        perms = perms.filter((x) => x !== "asi");
      }
      if (perms.join(",") === (p.permissions_required || []).join(",")) return p;
      return { ...p, permissions_required: perms };
    });
  }, [form.heritage_site]);

  const sendEmailOtp = async () => {
    if (!form.email?.trim()) {
      toast({ title: "Enter email first", status: "warning" });
      return;
    }
    const res = await fetch(`${urlBase}/leads/send-booking-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: form.email.trim() }),
    });
    const j = await res.json();
    if (!res.ok || !j.success) {
      toast({ title: j.message || "Could not send OTP", status: "error" });
      return;
    }
    toast({ title: "OTP sent to email", status: "success" });
  };

  const verifyEmailOtp = async () => {
    const res = await fetch(`${urlBase}/leads/verify-booking-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: form.email.trim(), otp: emailOtp }),
    });
    const j = await res.json();
    if (!res.ok || !j.success) {
      toast({ title: j.message || "Invalid OTP", status: "error" });
      return;
    }
    setF({ email_verified: true });
    toast({ title: "Email verified", status: "success" });
  };

  const submitFinal = async () => {
    for (let s = 0; s <= 10; s++) {
      const e = validateStep(s);
      if (e) {
        toast({ title: "Complete earlier steps", description: e, status: "warning" });
        setStep(s);
        return;
      }
    }
    const legalDocs = docs.legal || {};
    if (!legalDocs.undertaking_form) {
      toast({ title: "Upload undertaking form", status: "warning" });
      setStep(10);
      return;
    }
    await saveDraft();
    const submitId = recordIdRef.current;
    if (!submitId) {
      toast({ title: "Could not save application", status: "error" });
      return;
    }
    try {
      const { res, data: j } = await fetchApiJson(`${permBase}/update/${submitId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...persistPayload(), status: "submitted" }),
      });
      if (!res.ok || j.isError) {
        toast({ title: j.message || "Submission blocked", status: "error", duration: 6000 });
        return;
      }
      navigate("/location-permission/success", { state: { ref: j.data?.request_ref || requestRef, id: submitId } });
    } catch (e) {
      toast({ title: "Submit failed", description: e.message, status: "error", duration: 8000 });
    }
  };

  const syncDronePermission = (cam) => {
    setForm((p) => {
      const hasDroneCam = (cam || []).includes("Drone");
      let perms = [...(p.permissions_required || [])];
      if (hasDroneCam && !perms.includes("drone")) perms.push("drone");
      return {
        ...p,
        equipment: { ...p.equipment, cameraTypes: cam },
        permissions_required: perms,
      };
    });
  };

  const renderUploadRow = (label, slot, accept = ".pdf,.jpg,.jpeg,.png,.webp") => (
    <FormControl key={slot}>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <HStack>
        <Button as="label" leftIcon={<FiUpload />} size="sm" variant="outline" cursor="pointer">
          Choose file
          <Input type="file" hidden accept={accept} onChange={(e) => upload(e.target.files?.[0], slot)} />
        </Button>
        {docs[slot.split(".")[0]]?.[slot.split(".")[1]] ? (
          <Badge colorScheme="green"><FiCheck /></Badge>
        ) : null}
      </HStack>
    </FormControl>
  );

  const stepBody = () => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Project title</FormLabel>
              <Input value={form.project_title} onChange={(e) => setF({ project_title: e.target.value })} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Project type</FormLabel>
              <select
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }}
                value={form.project_type}
                onChange={(e) => setF({ project_type: e.target.value })}
              >
                <option value="">Select…</option>
                {PROJECT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl><FormLabel>Production house</FormLabel><Input value={form.production_house} onChange={(e) => setF({ production_house: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Producer</FormLabel><Input value={form.producer_name} onChange={(e) => setF({ producer_name: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Director</FormLabel><Input value={form.director_name} onChange={(e) => setF({ director_name: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Line producer</FormLabel><Input value={form.line_producer} onChange={(e) => setF({ line_producer: e.target.value })} /></FormControl>
            </SimpleGrid>
            <FormControl isRequired>
              <FormLabel>Languages</FormLabel>
              <CheckboxGroup
                value={toLanguageArray(form.language)}
                onChange={(v) => setF({ language: toLanguageArray(v) })}
              >
                <Wrap spacing={3} shouldWrapChildren>{LANGS.map((l) => (
                  <WrapItem key={l}>
                    <Checkbox value={l} sx={{ ".chakra-checkbox__label": { width: "100%" } }}>{l}</Checkbox>
                  </WrapItem>
                ))}</Wrap>
              </CheckboxGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea rows={4} value={form.description} onChange={(e) => setF({ description: e.target.value })} placeholder="Short brief — optional" />
            </FormControl>
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Applicant</FormLabel>
              <HStack>
                <Button size="sm" variant={form.applicant_type === "individual" ? "solid" : "outline"} onClick={() => setF({ applicant_type: "individual" })}>Individual</Button>
                <Button size="sm" variant={form.applicant_type === "company" ? "solid" : "outline"} onClick={() => setF({ applicant_type: "company" })}>Company</Button>
              </HStack>
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input type="email" value={form.email} onChange={(e) => setF({ email: e.target.value, email_verified: false })} />
                <Stack direction={{ base: "column", sm: "row" }} mt={2} spacing={2} align={{ sm: "center" }} flexWrap="wrap">
                  <Button size="xs" onClick={sendEmailOtp}>Send OTP</Button>
                  <Input placeholder="Email OTP" size="sm" maxW={{ base: "100%", sm: "120px" }} value={emailOtp} onChange={(e) => setEmailOtp(e.target.value)} />
                  <Button size="xs" onClick={verifyEmailOtp}>Verify</Button>
                  {form.email_verified ? <Badge colorScheme="green">Verified</Badge> : null}
                </Stack>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Contact number</FormLabel>
                <Input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={form.contact_number}
                  onChange={(e) => setF({ contact_number: e.target.value })}
                />
              </FormControl>
            </SimpleGrid>
            <Divider />
            <Text fontWeight="bold">Identity uploads</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {renderUploadRow("Aadhaar", "identity.aadhaar")}
              {renderUploadRow("PAN", "identity.pan")}
              {renderUploadRow("Producer card / Film ID", "identity.producer_card")}
              {renderUploadRow("Authority letter", "identity.authority_letter")}
              {renderUploadRow("Company letterhead (PDF)", "identity.company_letterhead", ".pdf")}
              {form.applicant_type === "company" ? renderUploadRow("GST certificate", "identity.gst_certificate") : null}
              {renderUploadRow("Address proof", "identity.address_proof")}
            </SimpleGrid>
          </Stack>
        );
      case 2:
        return (
          <Stack spacing={4}>
            {!isLoaded ? <Text>Loading maps…</Text> : (
              <Autocomplete
                onLoad={(ac) => { autocompleteRef.current = ac; }}
                onPlaceChanged={onPlaceChanged}
              >
                <FormControl isRequired>
                  <FormLabel><HStack><Icon as={FiMapPin} />Location name (Google Places)</HStack></FormLabel>
                  <Input ref={locRef} placeholder="Search place" />
                </FormControl>
              </Autocomplete>
            )}
            <FormControl isRequired>
              <FormLabel>Full address</FormLabel>
              <Textarea value={form.address} onChange={(e) => setF({ address: e.target.value })} rows={3} />
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl><FormLabel>City</FormLabel><Input value={form.city} onChange={(e) => setF({ city: e.target.value })} /></FormControl>
              <FormControl><FormLabel>State</FormLabel><Input value={form.state} onChange={(e) => setF({ state: e.target.value })} /></FormControl>
            </SimpleGrid>
            <FormControl><FormLabel>Landmark</FormLabel><Input value={form.landmark} onChange={(e) => setF({ landmark: e.target.value })} /></FormControl>
            <FormControl isRequired>
              <FormLabel>Type of location</FormLabel>
              <select style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0" }} value={form.location_type} onChange={(e) => setF({ location_type: e.target.value })}>
                <option value="">Select…</option>
                {LOCATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0}>Indoor / outdoor</FormLabel>
              <HStack ml={4}>
                <Text fontSize="sm">Outdoor</Text>
                <Switch isChecked={form.indoor_outdoor === "indoor"} onChange={(e) => setF({ indoor_outdoor: e.target.checked ? "indoor" : "outdoor" })} />
                <Text fontSize="sm">Indoor</Text>
              </HStack>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Area usage</FormLabel>
              <select
                style={{ width: "100%", padding: "10px", borderRadius: "8px" }}
                value={form.area_usage?.mode || "full"}
                onChange={(e) =>
                  setF({ area_usage: { ...form.area_usage, mode: e.target.value } })
                }
              >
                <option value="full">Full area</option>
                <option value="partial">Partial area</option>
              </select>
            </FormControl>
            {form.area_usage?.mode === "partial" ? (
              <FormControl isRequired>
                <FormLabel>Partial area details</FormLabel>
                <Input
                  value={form.area_usage?.partialDetail || ""}
                  onChange={(e) =>
                    setF({ area_usage: { ...form.area_usage, partialDetail: e.target.value } })
                  }
                />
              </FormControl>
            ) : null}
          </Stack>
        );
      case 3:
        return (
          <Stack spacing={4}>
            <Checkbox isChecked={form.heritage_site} onChange={(e) => setF({ heritage_site: e.target.checked })}>
              Shooting at a heritage / ASI site
            </Checkbox>
            <CheckboxGroup
              value={form.permissions_required}
              onChange={(v) => setF({ permissions_required: v })}
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                {PERMISSION_OPTS.map((o) => (
                  <Checkbox key={o.v} value={o.v} isDisabled={o.v === "asi" && !form.heritage_site}>
                    {o.label}
                  </Checkbox>
                ))}
              </SimpleGrid>
            </CheckboxGroup>
            {form.near_airport_hint ? (
              <Box bg="blue.50" p={3} borderRadius="md" fontSize="sm">
                Location may be near airport infrastructure — Airport Authority is pre-selected; adjust if not needed.
              </Box>
            ) : null}
          </Stack>
        );
      case 4:
        return (
          <Stack spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Start</FormLabel>
                <Input type="datetime-local" value={form.start_datetime} onChange={(e) => setF({ start_datetime: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>End</FormLabel>
                <Input type="datetime-local" value={form.end_datetime} onChange={(e) => setF({ end_datetime: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            <HStack>
              <Text fontWeight="semibold">Shoot days (auto):</Text>
              <Badge>{form.total_days || "—"}</Badge>
            </HStack>
            <FormControl isRequired>
              <FormLabel>Daily timing</FormLabel>
              <Input type="time" value={form.daily_timing} onChange={(e) => setF({ daily_timing: e.target.value })} />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0}>Night shoot</FormLabel>
              <Switch isChecked={form.night_shoot} onChange={(e) => setF({ night_shoot: e.target.checked })} ml={2} />
            </FormControl>
          </Stack>
        );
      case 5:
        return (
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Total crew size</FormLabel>
              <NumberInput value={form.crew_size} onChange={(_, v) => setF({ crew_size: v })} min={1}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <Text fontWeight="bold">Key crew</Text>
            {form.crew_details.map((c, i) => (
              <HStack key={i}>
                <Input placeholder="Name" value={c.name} onChange={(e) => {
                  const next = [...form.crew_details];
                  next[i] = { ...next[i], name: e.target.value };
                  setF({ crew_details: next });
                }} />
                <select
                  style={{ padding: "8px", borderRadius: "8px", minWidth: "160px" }}
                  value={c.role}
                  onChange={(e) => {
                    const next = [...form.crew_details];
                    next[i] = { ...next[i], role: e.target.value };
                    setF({ crew_details: next });
                  }}
                >
                  <option value="">Role…</option>
                  {CREW_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </HStack>
            ))}
            <Button
              size="sm"
              onClick={() => setF({ crew_details: [...form.crew_details, { name: "", role: "" }] })}
            >
              + Add crew
            </Button>
          </Stack>
        );
      case 6:
        return (
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Camera type</FormLabel>
              <CheckboxGroup value={form.equipment?.cameraTypes || []} onChange={(v) => syncDronePermission(v)}>
                <Wrap>{CAMERA_OPTS.map((c) => <WrapItem key={c}><Checkbox value={c}>{c}</Checkbox></WrapItem>)}</Wrap>
              </CheckboxGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Equipment tags</FormLabel>
              <HStack>
                <Input
                  placeholder="Type and press Enter"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const t = tagInput.trim();
                      if (!t) return;
                      const tags = [...(form.equipment?.equipmentTags || []), t];
                      setF({ equipment: { ...form.equipment, equipmentTags: tags } });
                      setTagInput("");
                    }
                  }}
                />
              </HStack>
              <Wrap mt={2}>
                {(form.equipment?.equipmentTags || []).map((t) => (
                  <WrapItem key={t}>
                    <Badge cursor="pointer" onClick={() => {
                      const tags = (form.equipment?.equipmentTags || []).filter((x) => x !== t);
                      setF({ equipment: { ...form.equipment, equipmentTags: tags } });
                    }}>{t} ×</Badge>
                  </WrapItem>
                ))}
              </Wrap>
            </FormControl>
            {(form.permissions_required?.includes("drone") || form.equipment?.cameraTypes?.includes("Drone")) ? (
              <Card variant="outline" borderColor="purple.200">
                <CardHeader py={2}><Heading size="sm">Drone details</Heading></CardHeader>
                <CardBody pt={0}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl><FormLabel>UA registration / ID</FormLabel><Input value={form.equipment?.droneDetails?.registrationId || ""} onChange={(e) => setF({ equipment: { ...form.equipment, droneDetails: { ...form.equipment.droneDetails, registrationId: e.target.value } } })} /></FormControl>
                    <FormControl><FormLabel>Pilot name</FormLabel><Input value={form.equipment?.droneDetails?.pilotName || ""} onChange={(e) => setF({ equipment: { ...form.equipment, droneDetails: { ...form.equipment.droneDetails, pilotName: e.target.value } } })} /></FormControl>
                  </SimpleGrid>
                </CardBody>
              </Card>
            ) : null}
            <FormControl>
              <FormLabel>Lighting</FormLabel>
              <select style={{ width: "100%", padding: "10px", borderRadius: "8px" }} value={form.lighting} onChange={(e) => setF({ lighting: e.target.value })}>
                <option value="normal">Normal</option>
                <option value="heavy">Heavy</option>
              </select>
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0}>Generator required</FormLabel>
              <Switch isChecked={form.generator_required} onChange={(e) => setF({ generator_required: e.target.checked })} ml={2} />
            </FormControl>
          </Stack>
        );
      case 7:
        return (
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Number of vehicles</FormLabel>
              <NumberInput value={form.vehicles?.count} onChange={(_, v) => setF({ vehicles: { ...form.vehicles, count: v } })} min={0}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel>Vehicle types</FormLabel>
              <CheckboxGroup value={form.vehicles?.types || []} onChange={(v) => setF({ vehicles: { ...form.vehicles, types: v } })}>
                <Wrap>{VEHICLE_OPTS.map((t) => <WrapItem key={t}><Checkbox value={t}>{t}</Checkbox></WrapItem>)}</Wrap>
              </CheckboxGroup>
            </FormControl>
            <FormControl display="flex" alignItems="center"><FormLabel mb={0}>Parking required</FormLabel><Switch isChecked={form.vehicles?.parkingRequired} onChange={(e) => setF({ vehicles: { ...form.vehicles, parkingRequired: e.target.checked } })} ml={2} /></FormControl>
            <FormControl display="flex" alignItems="center"><FormLabel mb={0}>Entry permission needed</FormLabel><Switch isChecked={form.vehicles?.entryPermission} onChange={(e) => setF({ vehicles: { ...form.vehicles, entryPermission: e.target.checked } })} ml={2} /></FormControl>
          </Stack>
        );
      case 8:
        return (
          <Stack spacing={3}>
            {Object.entries({
              loudSound: "Loud sound / music",
              roadBlocking: "Road blocking",
              crowdControl: "Crowd control",
              stunts: "Stunts / action",
              fireExplosives: "Fire / explosives",
              animals: "Animals",
            }).map(([k, label]) => (
              <FormControl key={k} display="flex" alignItems="center">
                <FormLabel mb={0}>{label}</FormLabel>
                <Switch
                  isChecked={!!form.special_requirements?.[k]}
                  onChange={(e) =>
                    setF({
                      special_requirements: { ...form.special_requirements, [k]: e.target.checked },
                    })
                  }
                  ml="auto"
                />
              </FormControl>
            ))}
            {form.special_requirements?.fireExplosives ? (
              <Flex align="center" gap={2} bg="orange.50" p={3} borderRadius="md" color="orange.900">
                <FiAlertTriangle /><Text fontSize="sm" fontWeight="medium">Pyrotechnics require extra clearances — authorities may need additional NOCs.</Text>
              </Flex>
            ) : null}
          </Stack>
        );
      case 9:
        return (
          <Stack spacing={4}>
            <FormControl display="flex" alignItems="center"><FormLabel mb={0}>Electricity required</FormLabel><Switch isChecked={form.utilities?.electricity} onChange={(e) => setF({ utilities: { ...form.utilities, electricity: e.target.checked } })} ml={2} /></FormControl>
            <FormControl><FormLabel>Generator usage</FormLabel><Input placeholder="e.g. 5 KVA × 2" value={form.utilities?.generatorUsage || ""} onChange={(e) => setF({ utilities: { ...form.utilities, generatorUsage: e.target.value } })} /></FormControl>
            <FormControl><FormLabel>Water requirement</FormLabel><select style={{ width: "100%", padding: "10px", borderRadius: "8px" }} value={form.utilities?.water || ""} onChange={(e) => setF({ utilities: { ...form.utilities, water: e.target.value } })}>
              <option value="">Select…</option>
              <option value="none">None</option>
              <option value="drinking">Drinking only</option>
              <option value="production">Production use</option>
            </select></FormControl>
            <FormControl><FormLabel>Washroom setup</FormLabel><select style={{ width: "100%", padding: "10px", borderRadius: "8px" }} value={form.utilities?.washroom || ""} onChange={(e) => setF({ utilities: { ...form.utilities, washroom: e.target.value } })}>
              <option value="">Select…</option>
              <option value="existing">Use existing</option>
              <option value="portable">Portable units</option>
            </select></FormControl>
          </Stack>
        );
      case 10:
        return (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {renderUploadRow("Indemnity bond", "legal.indemnity_bond")}
            {renderUploadRow("Insurance certificate (mandatory)", "legal.insurance_certificate")}
            {renderUploadRow("Undertaking form", "legal.undertaking_form")}
          </SimpleGrid>
        );
      case 11:
        return (
          <Stack spacing={4}>
            {renderUploadRow("Previous permissions (optional)", "final.previous_permissions")}
            <Divider />
            <Heading size="sm">Summary</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
              <Text><b>Title</b> {form.project_title}</Text>
              <Text><b>Type</b> {form.project_type}</Text>
              <Text><b>Location</b> {form.location_name}</Text>
              <Text><b>Schedule</b> {form.total_days} days</Text>
              <Text><b>Request ID</b> {requestRef || "…"}</Text>
            </SimpleGrid>
            <Button colorScheme="blue" onClick={submitFinal}>Submit application</Button>
          </Stack>
        );
      default:
        return null;
    }
  };

  const pct = ((step + 1) / STEP_LABELS.length) * 100;

  const mainBottomPad = useBreakpointValue({
    base: "calc(76px + 72px + env(safe-area-inset-bottom, 0px) + 24px)",
    md: "120px",
  });

  return (
    <Box bg="gray.50" minH="100vh" pb={mainBottomPad ?? "120px"} px={{ base: 3, md: 4 }}>
      <Header />
      <Container maxW="container.md" py={{ base: 5, md: 8 }}>
        <VStack align="stretch" spacing={6}>
          <Box>
            <Heading size="lg" mb={1}>Location permission</Heading>
            <Text color="gray.600" fontSize="sm">Smart application — progress saves automatically.</Text>
          </Box>
          <Box>
            <Progress value={pct} size="sm" colorScheme="blue" borderRadius="full" hasStripe />
            <Box overflowX="auto" mt={2} mx={{ base: -1, md: 0 }} px={1} css={{ WebkitOverflowScrolling: "touch" }}>
              <HStack as="ol" spacing={3} minW="max-content" fontSize="xs" color="gray.500" pl={0} listStyleType="none" m={0}>
                {STEP_LABELS.map((l, i) => (
                  <Text as="li" key={l} fontWeight={i === step ? "bold" : "normal"} color={i === step ? "blue.600" : undefined} whiteSpace="nowrap">
                    {i + 1}. {l}
                  </Text>
                ))}
              </HStack>
            </Box>
          </Box>
          <Card shadow="md" borderRadius="xl">
            <CardHeader pb={0}>
              <Heading size="md">{STEP_LABELS[step]}</Heading>
              {requestRef ? <Badge mt={2} colorScheme="purple">{requestRef}</Badge> : null}
            </CardHeader>
            <CardBody pb={{ base: 4, md: 6 }}>{stepBody()}</CardBody>
          </Card>
          {saving ? <Text fontSize="xs" color="gray.500">Saving draft…</Text> : null}
        </VStack>
      </Container>
      <Box
        position="fixed"
        bottom={footerBottom ?? 0}
        left={0}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        py={3}
        px={4}
        zIndex={footerZ ?? 1002}
        boxShadow="0 -4px 20px rgba(0,0,0,0.08)"
        sx={{ pb: "max(12px, env(safe-area-inset-bottom, 0px))" }}
      >
        <Container maxW="container.md" px={{ base: 2, md: 4 }}>
          <Flex justify="space-between" gap={3} flexWrap="wrap">
            <Button flex={{ base: 1, md: "unset" }} minW={{ base: "44%", md: "auto" }} variant="outline" size={{ base: "lg", md: "md" }} onClick={goBack} isDisabled={step === 0}>
              Previous
            </Button>
            {step < STEP_LABELS.length - 1 ? (
              <Button flex={{ base: 1, md: "unset" }} minW={{ base: "44%", md: "auto" }} colorScheme="blue" size={{ base: "lg", md: "md" }} onClick={goNext}>
                Next
              </Button>
            ) : null}
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}
